"use client"

import { useGetAllSubTraining } from "@/actions/sub-training"
import { useGetTrainingByCodeCombobox } from "@/actions/training"
import {
  useGetAllTypeTrainingComboBox,
  useGetTypeAllTrainingByNameComboBox,
} from "@/actions/type-training"
import { Button } from "@/components/ui/button"
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import { formatDotString } from "@/lib/string"
import { cn } from "@/lib/tailwind-utils"
import { Check } from "lucide-react"
import { useRouter } from "next/navigation"
import { useState } from "react"
import { AiOutlineLoading3Quarters } from "react-icons/ai"

function PelatihanCombobox({
  name,
  className,
  placeholder,
  searchPlaceholder,
  isLoading = false,
  isSuccess = false,
  isError = false,
  value,
  setValue,
  choosedValue,
  handleChoosedValue,
  datas = [],
}) {
  const [open, setOpen] = useState(false)

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="reset"
          role="combobox"
          aria-expanded={open}
          className={cn(
            "w-full flex h-full items-center justify-between",
            className,
          )}>
          <span className="truncate">
            {value
              ? formatDotString(
                  datas.find((data) => data.value === value)?.label,
                  50,
                )
              : placeholder}
          </span>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] max-h-[--radix-popover-content-available-height] p-0" align="start">
        <Command>
          <CommandInput placeholder={searchPlaceholder} />
          {isLoading && (
            <CommandList>
              <CommandGroup>
                {Array.from({ length: 1 }).map((_, index) => (
                  <CommandItem key={index + 1}>
                    <AiOutlineLoading3Quarters className="mr-2 h-4 w-4 animate-spin" />{" "}
                    Sedang memuat data...
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          )}
          {isSuccess && (
            <CommandList>
              <CommandEmpty>
                {name ? `${name} tidak ditemukan.` : "Data tidak ditemukan."}
              </CommandEmpty>
              <CommandGroup>
                {datas.map((data) => {
                  return (
                    <CommandItem
                      key={data.value}
                      value={data.value}
                      onSelect={(currentValue) => {
                        setValue(currentValue === value ? "" : currentValue)
                        handleChoosedValue(
                          choosedValue?.label === data?.label ? null : data,
                        )
                        setOpen(false)
                      }}>
                      <Check
                        className={cn(
                          "mr-2 h-4 w-4 shrink-0",
                          value === data.value ? "opacity-100" : "opacity-0",
                        )}
                      />
                      <span className="truncate">{data.label}</span>
                    </CommandItem>
                  )
                })}
              </CommandGroup>
            </CommandList>
          )}
        </Command>
      </PopoverContent>
    </Popover>
  )
}

export default function PelatihanSearchBar({ className = "" }) {
  const router = useRouter()
  const [choosedSubtraining, setChoosedSubTraining] = useState({})
  const [subTrainingSearch, setSubTrainingSearch] = useState("")
  const {
    data: allSubTraining,
    isFetching: isLoadingGetAllSubTraining,
    isSuccess: isSuccessGetAllSubTraining,
  } = useGetAllSubTraining()

  const handleChooseSubTraining = (value) => {
    if (!value) {
      setChoosedSubTraining({})
    }
    setChoosedSubTraining(value)
  }

  const handleSearchPelatihan = () => {
    let url = "/pelatihan"

    if (choosedSubtraining?.id) {
      return router.push(
        `/pelatihan/${String(choosedSubtraining?.subTrainingCode).toLowerCase()}`,
      )
    }

    return router.push(url)
  }

  return (
    <div className={cn("w-full max-w-4xl relative z-20 mt-12", className)}>
      <div className="bg-white/10 backdrop-blur-2xl rounded-[2rem] p-6 lg:p-8 border border-white/20 shadow-[0_20px_50px_-15px_rgba(0,0,0,0.4)] transition-all duration-300 transform group">
        <div className="text-center mb-6">
          <h3 className="text-xl md:text-2xl font-bold text-white mb-2 shadow-sm drop-shadow-md">
            Pencarian Spesifik
          </h3>
          <p className="text-blue-100/80 text-sm md:text-base font-light">
            Cari judul atau topik pelatihan secara langsung
          </p>
        </div>

        <div className="bg-black/20 backdrop-blur-md rounded-2xl p-2 border border-white/10 shadow-inner">
          <div className="grid lg:grid-cols-7 grid-cols-1 gap-2 items-stretch">
            <div className="lg:col-span-5 relative group/input h-[56px]">
              <div className="absolute left-4 top-1/2 -translate-y-1/2 text-cyan-500 z-10 pointer-events-none transition-transform group-focus-within/input:scale-110">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
              <PelatihanCombobox
                className="w-full h-full bg-white text-slate-800 rounded-xl pl-12 pr-4 border-2 border-transparent focus:border-cyan-400 hover:border-cyan-200 transition-all font-medium text-base h-full"
                name="Pelatihan"
                placeholder="Cari program pelatihan..."
                searchPlaceholder="Ketik nama pelatihan..."
                isLoading={isLoadingGetAllSubTraining}
                isSuccess={isSuccessGetAllSubTraining}
                value={subTrainingSearch}
                setValue={setSubTrainingSearch}
                choosedValue={choosedSubtraining}
                handleChoosedValue={handleChooseSubTraining}
                datas={allSubTraining}
              />
            </div>
            <div className="lg:col-span-2 h-[56px]">
              <Button
                type="button"
                onClick={handleSearchPelatihan}
                className="w-full h-full rounded-xl bg-gradient-to-r from-cyan-500 to-blue-600 text-white hover:from-cyan-400 hover:to-blue-500 font-bold text-base transition-all duration-300 transform hover:scale-[1.02] shadow-lg border border-cyan-400/50 flex items-center justify-center gap-2">
                Temukan
                <svg className="w-4 h-4 transition-transform group-hover:-translate-y-1 group-hover:translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
